import flatMap from 'lodash/flatMap';

import { Nullable, ReservationData } from 'chaika-frontend-types/frontend-cms';
import { OptionType } from '@components/Select';

import { translate } from '@chaika/i18n/helper';

import { store } from '@store';
import { calculateAvailableReservationDateInterval } from '@patient/store/entry/calculateAvailableReservationDateInterval';
import { ehrService } from '@patient/services/ehr/ehrService';
import { serviceService } from '@services/services/serviceService';
import { DateHelper } from '@utils/DateHelper';

import { referralService } from './referralService';
import { renderedServicesService } from './renderedServicesService';

import { EntryApi } from '@api/EntryApi';
import { ReservationApi } from '@api/ReservationApi';

class EntryReservationService {
    public async load(): Promise<void> {
        const { entry, authUser } = store;
        const { id: entryId, ehrId: patientId, creationTime, visitTime } = entry;
        const { id, isAssistant, hasRootAccess } = authUser;

        const { from, to } = calculateAvailableReservationDateInterval(visitTime ?? creationTime);

        const userId = isAssistant || hasRootAccess ? String(null) : id;

        const [{ reservations = [] }, [entryReservation]] = await Promise.all([
            EntryApi.findReservations({ patientId, userId, from, to }),
            ReservationApi.find({ patientId, entryId, from }),
        ]);

        await Promise.all([
            serviceService.loadByIds(flatMap([...reservations, entryReservation], 'serviceIds')),
            entryReservation &&
                ehrService.loadInformedConsentForReservation(entryReservation.id, entryReservation.serviceIds[0]),
        ]);

        entry.availableReservations = reservations;
        entry.reservation = entryReservation;
    }

    public async updateEntryReservation(reservationId?: string): Promise<void> {
        const { entry } = store;
        const { id: entryId, reservation: currentReservation, availableReservations } = entry;

        const entryReservation = reservationId
            ? availableReservations.find(({ id }) => id === reservationId)
            : undefined;

        await Promise.all([
            currentReservation &&
                EntryApi.removeEntryReservation(entryId, currentReservation.id, currentReservation.referralId),
            entryReservation && EntryApi.setEntryReservation(entryId, entryReservation),
            entryReservation &&
                ehrService.loadInformedConsentForReservation(entryReservation.id, entryReservation.serviceIds[0]),
        ]);

        if (currentReservation && entry.availableReservations.every(({ id }) => id !== currentReservation.id)) {
            entry.availableReservations.push(currentReservation);
        }

        entry.reservation = entryReservation;

        await Promise.all([referralService.reloadReferrals(), renderedServicesService.updateStore()]);
    }

    public getReservationsOptions(): OptionType<Nullable<string>>[] {
        const { availableReservations, reservation } = store.entry;
        const tLabel = translate('withoutReservationLabel');

        const options = [
            {
                value: null,
                label: tLabel,
            },
            ...availableReservations.map(reservation => ({
                value: reservation.id,
                label: this.makeReservationLabel(reservation),
            })),
        ];

        return reservation
            ? [
                  ...options,
                  {
                      value: reservation.id,
                      label: this.makeReservationLabel(reservation),
                  },
              ]
            : options;
    }

    private makeReservationLabel({ startTime, serviceIds }: ReservationData): string {
        const {
            serviceStore: { serviceList },
        } = store;

        return `${DateHelper.getTime(startTime)} ${DateHelper.toLocalStringDate(startTime)} ${serviceIds
            .map(id => serviceList.get(id)?.name ?? '')
            .join('; ')}`;
    }
}

export const entryReservationService = new EntryReservationService();
